import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, downloadBlobResponse, formatApiErrorDetail } from "@/lib/api";
import { bolehKelolaSurvei } from "@/lib/roles";
import SurveiRekapTabel from "@/components/SurveiRekapTabel";
import {
    KELOMPOK_KEDOKTERAN, kepnakesCocok, labelTanpaLurus, periodeGabungan, pesanGagalUnduhSurvei, TEKS_BANTU_UNDUH_SURVEI,
    TEKS_SYARAT_GABUNGAN,
} from "@/lib/survei";

// Tab "Gabungan" (PR #113, Super Admin): seluruh peserta didik (Kedokteran + Kepnakes) -- indikator "Tingkat Kepuasan
// Peserta Didik". Hanya periode yg DITUTUP; periode Kepnakes yg ditawarkan hanya yg versi butirnya SAMA dgn periode
// Kedokteran terpilih (server menegakkan aturan yg sama). Angka & aturan kelompok kecil dari server (SATU perhitungan dgn
// Excel Gabungan). Hasil terpisah (tab Hasil) tetap yang utama.
export default function SurveiGabungan({ periode }) {
    const { user } = useAuth();
    const [kedId, setKedId] = useState("");
    const [kepId, setKepId] = useState("");
    const [tanpaLurus, setTanpaLurus] = useState(false);
    const [data, setData] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const daftarKed = periodeGabungan(periode, KELOMPOK_KEDOKTERAN);
    const ked = daftarKed.find((p) => p.id === kedId) || null;
    const daftarKep = kepnakesCocok(periode, ked);
    const siap = !!ked && daftarKep.some((p) => p.id === kepId);

    useEffect(() => { setKepId(""); }, [kedId]);
    useEffect(() => {
        setData(null);
        if (!siap) return undefined;
        let batal = false;
        api.get("/survei/gabungan/hasil", { params: { kedokteran_id: kedId, kepnakes_id: kepId, tanpa_lurus: tanpaLurus } })
            .then((r) => { if (!batal) setData(r.data); })
            .catch((e) => { if (!batal) toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
        return () => { batal = true; };
    }, [siap, kedId, kepId, tanpaLurus]);

    if (!bolehKelolaSurvei(user?.role)) return null;
    const unduh = async () => {
        setSibuk(true);
        try {
            const res = await api.get("/survei/gabungan/ekspor", {
                params: { kedokteran_id: kedId, kepnakes_id: kepId, tanpa_lurus: tanpaLurus ? 1 : 0 }, responseType: "blob" });
            downloadBlobResponse(res, "survei-gabungan.xlsx");
        } catch (e) {
            toast.error(pesanGagalUnduhSurvei(e?.response?.status));
        } finally {
            setSibuk(false);
        }
    };
    const pilih = (id, nilai, ubah, daftar, placeholder) => (
        <Select value={nilai} onValueChange={ubah} disabled={!daftar.length}>
            <SelectTrigger className="w-full sm:w-80" data-testid={id}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent>{daftar.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
        </Select>
    );
    let pesan = null;
    if (!daftarKed.length || !periodeGabungan(periode, "kepnakes").length) pesan = TEKS_SYARAT_GABUNGAN;
    else if (ked && !daftarKep.length) pesan = "Tidak ada periode Kepnakes yang sudah ditutup dengan versi butir yang sama dengan periode Kedokteran ini.";

    return (
        <div className="space-y-4" data-testid="survei-gabungan">
            <p className="text-sm text-slate-600">
                Seluruh peserta didik (Kedokteran + Kepnakes), dihitung dari semua responden kedua periode dengan rumus yang sama.
                Hasil terpisah di tab Hasil tetap menjadi hasil utama.
            </p>
            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
                <div className="flex flex-wrap items-center gap-2"><span className="w-24 text-sm text-slate-600">Kedokteran</span>
                    {pilih("survei-gabungan-ked", kedId, setKedId, daftarKed, "Pilih periode Kedokteran")}</div>
                <div className="flex flex-wrap items-center gap-2"><span className="w-24 text-sm text-slate-600">Kepnakes</span>
                    {pilih("survei-gabungan-kep", kepId, setKepId, daftarKep, "Pilih periode Kepnakes")}</div>
                <div className="flex items-center gap-2">
                    <Switch id="survei-gabungan-lurus" checked={tanpaLurus} onCheckedChange={setTanpaLurus} data-testid="survei-gabungan-lurus" />
                    <Label htmlFor="survei-gabungan-lurus">{labelTanpaLurus(data?.jumlah_butir || ked?.versi_butir?.jumlah_butir)}</Label>
                </div>
            </div>
            {pesan && <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="survei-gabungan-syarat">{pesan}</p>}
            {siap && (
                <div className="space-y-2 rounded-lg border border-slate-200 p-4">
                    <Button onClick={unduh} disabled={sibuk} aria-busy={sibuk} data-testid="survei-gabungan-unduh" className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]">
                        {sibuk ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="mr-2 h-4 w-4" aria-hidden="true" />}
                        {sibuk ? "Menyiapkan berkas…" : "Unduh Excel Gabungan"}
                    </Button>
                    <p className="text-xs text-slate-500">{TEKS_BANTU_UNDUH_SURVEI}</p>
                </div>
            )}
            {siap && !data && <p className="py-4 text-sm text-slate-500">Memuat…</p>}
            {data && (
                <>
                    <SurveiRekapTabel data={data.rekap} label={`Rekap Gabungan — ${data.periode_kedokteran.nama} + ${data.periode_kepnakes.nama}`} />
                    {data.rekap.cukup && data.rekap.kolom.length === 1 && (
                        <Card className="p-3 text-xs text-slate-500">Salah satu kelompok belum punya responden; hanya kolom SELURUH PESERTA DIDIK yang ditampilkan.</Card>
                    )}
                </>
            )}
        </div>
    );
}
